import * as R from 'ramda';
import { createSelector } from 'reselect';

import {
  ArrowDropDownCircleIcon,
  AttachMoneyIcon,
  DoneAllIcon,
  FormatAlignLeftIcon,
  IconComponent,
  LooksOneIcon,
  RadioButtonCheckedIcon,
  ShortTextIcon,
  TitleIcon,
  TodayIcon,
} from '@atom/mui/icons';

export const inventorySchemas = R.pathOr([], ['inventorySchemas']);
export const inventorySchema = R.pathOr({}, ['inventorySchema']);
export const inventorySchemaDetail = R.pathOr({}, ['inventorySchemaDetail']);

export const DEFECT_MARKER_ID = 100;
export const PROTECTIVE_COATING_MARKER_ID = 200;
export const END_JOINT_MARKER_ID = 300;
export const WEARING_SURFACE_MARKER_ID = 400;

export const filterSchemaByMarkerId = (schema, ownProps) => {
  const markerId = ownProps.markerId;
  const isFhwaForm = !!ownProps.isFhwaForm;

  if (R.isNil(schema) || R.isEmpty(schema)) {
    return schema;
  }

  if (markerId) {
    return {
      ...schema,
      // @ts-ignore
      elementGroups: schema.elementGroups.reduce((acc, elementGroup) => {
        if (elementGroup.isGroup) {
          return acc;
        }

        const elementMarkerId = elementGroup.elements[0].markerId;

        const newElementGroup = {
          ...elementGroup,
          elements: elementGroup.elements.map(element => ({
            ...element,
            hasChildren: false,
          })),
        };

        return elementMarkerId === markerId ? [...acc, newElementGroup] : acc;
      }, []),
    };
  }

  if (isFhwaForm) {
    return {
      ...schema,
      // @ts-ignore
      elementGroups: schema.elementGroups.map(elementGroup => ({
        ...elementGroup,
        elements: elementGroup.elements.map(element => ({
          ...element,
          hasChildren: false,
        })),
      })),
    };
  }

  return schema;
};

export const getFilteredSchemaByMarkerId = (state, ownProps) =>
  filterSchemaByMarkerId(inventorySchema(state), ownProps);

export const getSchemaAttributeFilterGroupsSelector = createSelector(
  // @ts-ignore
  ownProps => ownProps.rootSchema,
  schema => {
    if (R.isNil(schema)) {
      return [];
    }

    return schema.attributeGroups
      .map(group => {
        const { name, attributes } = group;

        const filterableAttributes = attributes
          .map(id => ({
            id,
            ...schema.attributes[id],
          }))
          .filter(attribute => attribute.isFilterable);

        return {
          name,
          attributes: filterableAttributes,
        };
      })
      .filter(group => !R.isEmpty(group.attributes));
  },
);

export const mapElementProperties = element => ({
  id: element.id,
  name: element.name,
  assetType: element.assetType,
  attributeGroups: element.attributeGroups,
});

export const getElements = element => {
  const rootElement = mapElementProperties(element);

  if (R.isEmpty(element.elements)) {
    return [rootElement];
  }

  const elements = element.elements.map(elem => R.flatten(getElements(elem)));

  const groupedElements = element.elementGroups.reduce((acc, group) => {
    const elems = group.elements.map(elem => getElements(elem));
    return [...acc, ...R.flatten(elems)];
  }, []);

  return [rootElement, ...elements, ...groupedElements];
};

export const getInventorySchemasSelector = createSelector(
  inventorySchemas,
  schemas => [
    { id: 'all', name: 'All', attributes: {}, attributeGroups: [] },
    ...schemas,
  ],
);

export const getAttributeIconComponent = (dataType): IconComponent => {
  switch (dataType) {
    case 'date':
      return TodayIcon;
    case 'enummultiple':
      return DoneAllIcon;
    case 'boolean':
      return RadioButtonCheckedIcon;
    case 'number':
      return LooksOneIcon;
    case 'currency':
      return AttachMoneyIcon;
    case 'shorttext':
      return ShortTextIcon;
    case 'longtext':
      return FormatAlignLeftIcon;
    case 'enumsingle':
      return ArrowDropDownCircleIcon;
    default:
      return TitleIcon;
  }
};
